import React, { useEffect } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { setModalId, usePublicState } from '../../../store/slices/publicSlice';
import { useDispatch } from 'react-redux';
import CustomModal from '../../atoms/CustomModal';
import PillSeclection from '../ActionForm/PillSelection';
import Actions from '../ActionForm/Actions';
import FormField from '../../FormField/FormField';
import {
  useGetDisputeActionsQuery,
  useSubmitDisputeMutation,
} from '@/store/services/disputeService';

export default function BuyerDisputeModal() {
  const { t } = useTranslation();

  const dispatch = useDispatch();
  const { modalId } = usePublicState();

  const params = useParams();
  const disputeId = params?.disputeId;

  const [submit, { isLoading }] = useSubmitDisputeMutation();
  const { data: actions, isLoading: actionsLoading } =
    useGetDisputeActionsQuery({ disputeId });

  const schema = z
    .object({
      action: z.string().optional(),
      deduction: z.number().optional().nullable(),
      amountToSeller: z.number().optional().nullable(),
      compensationAmount: z.number().optional().nullable(),
      discount: z.number().optional().nullable(),
      statement: z
        .string()
        .transform((val) => val.replace(/^\s+/, ''))
        .optional(),
    })
    .superRefine(
      ({ action, statement, discount, compensationAmount, deduction }, ctx) => {
        if (!action)
          ctx.addIssue({
            message: t('components.disputeFeature.selectOneReason'),
            path: ['action'],
            code: 'custom',
          });

        if (!!action && ['DISCOUNT_SHIPMENT'].includes(action) && !discount)
          ctx.addIssue({
            message: t('components.validations.min1'),
            path: ['discount'],
            code: 'custom',
          });

        if (!!action && ['DEDUCTION'].includes(action) && !deduction)
          ctx.addIssue({
            message: t('components.validations.min1'),
            path: ['deduction'],
            code: 'custom',
          });

        if (
          !!action &&
          ['ADD_COMPENSATION'].includes(action) &&
          !compensationAmount
        )
          ctx.addIssue({
            message: t('components.validations.min1'),
            path: ['compensationAmount'],
            code: 'custom',
          });

        if (!statement)
          ctx.addIssue({
            message: t('components.validations.requiredField'),
            path: ['statement'],
            code: 'custom',
          });

        if (!!statement && statement.length < 50)
          ctx.addIssue({
            message: t('components.textareaMinAndMaxLength', {
              min: 50,
              max: 1000,
            }),
            path: ['statement'],
            code: 'custom',
          });
      }
    );

  type schemaType = z.infer<typeof schema>;

  const methods = useForm<schemaType>({
    resolver: zodResolver(schema),
    mode: 'all',
  });

  const [reasonValue] = useWatch({
    name: ['action'],
    control: methods.control,
  });

  const loading = isLoading || actionsLoading;
  // const loading = false;

  const handleSubmit = async (data: schemaType) => {
    const payload = {
      actionId: actions?.payload?.find((r) => r.code === data.action)?.id,
      sellerAmount: data?.amountToSeller,
      statement: data?.statement,
      deductionPercentage: data?.deduction,
      shipmentDiscount: data?.discount,
      buyerCompensation: data?.compensationAmount,
    };

    await submit({ disputeId, body: payload });

    window.location.reload();
  };

  const isModalOpened = modalId === 'DISPUTE_ACTION';

  useEffect(() => {
    if (isModalOpened) {
      methods.reset();
    }
  }, [isModalOpened]);

  if (!isModalOpened) return;

  return (
    <CustomModal
      isOpen={modalId === 'DISPUTE_ACTION'}
      onClose={() => {
        dispatch(setModalId({ id: null, extraData: {} }));
      }}
      subTitle={t('components.disputeFeature.editCommissionWarning')}
      title={t('components.disputeFeature.takeActionOnRequest')}
      centered
    >
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(handleSubmit)}>
          <div className="space-y-6 max-h-[600px] overflow-auto">
            <PillSeclection actions={actions?.payload} loading={false} />

            {reasonValue === 'DISCOUNT_SHIPMENT' && (
              <FormField
                placeholder={t('components.disputeFeature.set')}
                label={t('components.disputeFeature.discountShipmentFeeLabel')}
                type="number"
                name="discount"
                id="discount"
                min={0}
              />
            )}

            {reasonValue === 'DEDUCTION' && (
              <FormField
                placeholder={t('components.disputeFeature.set')}
                label={t('components.disputeFeature.setDeductionLabel')}
                type="number"
                name="deduction"
                id="deduction"
                min={0}
              />
            )}

            {reasonValue === 'ADD_COMPENSATION' && (
              <FormField
                placeholder={t('components.disputeFeature.set')}
                label={t('components.disputeFeature.compensationAmountLabel')}
                type="number"
                name="compensationAmount"
                id="compensationAmount"
                min={0}
              />
            )}

            {['ADD_COMPENSATION'].includes(reasonValue as string) && (
              <FormField
                label={t('components.disputeFeature.fundedBy')}
                type="text"
                name="fundedBy"
                id="fundedBy"
                disabled
                value="Seller"
              />
            )}

            {['RETURN_ADMIN'].includes(reasonValue as string) && (
              <FormField
                label={t('components.disputeFeature.shipmentFees')}
                type="text"
                name="shipmentFees"
                id="shipmentFees"
                disabled
                value="Seller"
              />
            )}

            {[
              'ADD_COMPENSATION',
              'DISCOUNT_SHIPMENT',
              'FREE_SHIPMENT',
              'RETURN_ADMIN',
            ].includes(reasonValue as string) && (
              <FormField
                placeholder={t('components.disputeFeature.amount')}
                label={t('components.disputeFeature.addAmountToSellerLabel')}
                type="number"
                name="amountToSeller"
                id="amountToSeller"
                optional
                min={0}
              />
            )}

            <FormField
              id="statement"
              name="statement"
              type="textarea"
              label={t('components.disputeFeature.arbitrationStatementLabel')}
              placeholder={t(
                'components.disputeFeature.arbitrationStatementPlaceholder'
              )}
              max={1000}
              min={50}
              maxLength={1000}
              helperText={t('components.disputeFeature.MinAndMax_Char')}
            />
          </div>

          <Actions loading={loading} />
        </form>
      </FormProvider>
    </CustomModal>
  );
}
